import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowRight,
  Bell,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  Clock,
  CornerDownLeft,
  FileText,
  ListTree,
  Plus,
  Search,
  User,
  X,
  Download,
  Smartphone,
  Mail,
  Share2,
} from 'lucide-react'
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useSnapshot } from 'valtio'
import { store } from '@/store'
import { Button } from '@/components/ui/button'
import { DateTimePicker } from '@/components/ui/date-time-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { AttachmentViewer } from './attachment-preview'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Skeleton } from '@/components/ui/skeleton'

const TaskLocations = lazy(() =>
  import('./task-locations').then((module) => ({ default: module.TaskLocations })),
)

const tabIndicatorTransition = {
  type: 'spring',
  stiffness: 420,
  damping: 34,
  mass: 0.6,
}
const tabPanelTransition = {
  duration: 0.2,
  ease: [0.22, 1, 0.36, 1],
}
const TabTriggerLabel = ({ tab, isActive }) => {
  const Icon = tab.icon

  return (
    <span
      className={cn(
        'relative z-10 flex min-w-0 items-center gap-1.5 transition-colors',
        isActive ? 'text-primary-foreground' : 'text-muted-foreground',
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate text-[12px] font-semibold">{tab.label}</span>
      {tab.badge && (
        <span className="rounded-full bg-foreground/10 px-1.5 py-0 text-[10px] font-semibold leading-tight">
          {tab.badge}
        </span>
      )}
    </span>
  )
}

const TabTrigger = ({ tab, activeTab, indicatorLayoutId }) => {
  const isActive = activeTab === tab.value

  return (
    <TabsTrigger
      value={tab.value}
      className="relative h-8 min-w-[70px] shrink-0 rounded-xl px-2 sm:px-2.5 text-muted-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none transition-none"
    >
      {isActive && (
        <motion.span
          layoutId={indicatorLayoutId}
          transition={tabIndicatorTransition}
          className="absolute inset-0 rounded-xl bg-primary shadow-sm"
        />
      )}
      <TabTriggerLabel tab={tab} isActive={isActive} />
    </TabsTrigger>
  )
}

const SubtaskProgress = ({ completedSubtasks, totalSubtasks }) => {
  const progress = totalSubtasks === 0 ? 0 : completedSubtasks / totalSubtasks

  return (
    <div className="mt-4 space-y-2.5 rounded-xl bg-muted/20 p-3 border border-border/20 shadow-inner">
      <div className="flex items-center justify-between text-[11px] font-semibold">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <ListTree className="h-3 w-3" />
          <span>Progress</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-primary">{completedSubtasks}</span>
          <span className="text-muted-foreground/40 font-normal">/</span>
          <span className="text-foreground">{totalSubtasks}</span>
          <span className="text-muted-foreground/60 font-normal ml-1">
            ({Math.round(progress * 100)}%)
          </span>
        </div>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted/50 border border-border/20">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-primary/80 to-primary origin-left shadow-[0_0_8px_rgba(var(--primary),0.4)]"
          initial={{
            scaleX: 0,
          }}
          animate={{
            scaleX: progress,
          }}
          transition={{
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1],
          }}
        />
      </div>
    </div>
  )
}

const SubtaskRow = ({
  subtask,
  handleToggleSubtaskClick,
  handleToggleSubtaskKeyDown,
  handleDeleteSubtaskClick,
}) => (
  <motion.div
    layout
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    className="group/subtask flex items-center gap-3 rounded-xl border border-border/40 bg-background/50 px-3 py-2 hover:border-border hover:bg-background hover:shadow-sm transition-all"
  >
    <div
      role="button"
      tabIndex={0}
      data-subtask-id={subtask.id}
      onClick={handleToggleSubtaskClick}
      onKeyDown={handleToggleSubtaskKeyDown}
      className="cursor-pointer shrink-0 transition-transform active:scale-90"
    >
      {subtask.completed ? (
        <div className="flex h-4 w-4 items-center justify-center rounded-full bg-primary/10">
          <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
        </div>
      ) : (
        <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30 hover:border-primary/50 transition-colors" />
      )}
    </div>

    <span
      className={cn(
        'min-w-0 flex-1 truncate text-[13px] font-medium transition-all',
        subtask.completed ? 'text-muted-foreground/60 line-through' : 'text-foreground',
      )}
    >
      {subtask.text}
    </span>

    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7 rounded-lg opacity-0 group-hover/subtask:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-all"
      data-subtask-id={subtask.id}
      onClick={handleDeleteSubtaskClick}
    >
      <X className="h-3.5 w-3.5" />
    </Button>
  </motion.div>
)

const SubtasksTabPanel = ({
  todo,
  subtaskText,
  subtaskCompletion,
  acceptSubtaskCompletion,
  handleAddSubtask,
  handleSubtaskTextInputChange,
  handleSubtaskInputKeyDown,
  handleToggleSubtaskClick,
  handleToggleSubtaskKeyDown,
  handleDeleteSubtaskClick,
}) => {
  const subtasks = todo.subtasks || []
  const completedSubtasks = subtasks.filter((subtask) => subtask.completed).length
  const totalSubtasks = subtasks.length

  const onKeyDown = (e) => {
    if (e.key === 'Tab' && subtaskCompletion) {
      e.preventDefault()
      const nextText = acceptSubtaskCompletion(subtaskText)
      handleSubtaskTextInputChange({ target: { value: nextText } })
      return
    }
    handleSubtaskInputKeyDown(e)
  }

  return (
    <div className="space-y-4">
      <div className="group relative flex items-center gap-3 rounded-2xl border border-border/20 bg-background/50 p-1 pl-4 shadow-sm transition-all focus-within:border-primary/50 focus-within:bg-background focus-within:ring-4 focus-within:ring-primary/5">
        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-primary group-focus-within:bg-primary group-focus-within:text-primary-foreground transition-colors">
          <Plus className="h-3.5 w-3.5" />
        </div>
        <div className="relative flex-1">
          {subtaskCompletion && (
            <div className="pointer-events-none absolute inset-0 z-0 px-0 py-2.5 text-[13px] font-medium leading-none whitespace-pre overflow-hidden">
              <span className="opacity-0">{subtaskText}</span>
              <span className="text-muted-foreground/50">{subtaskCompletion}</span>
            </div>
          )}
          <Input
            placeholder="Add a step..."
            value={subtaskText}
            onChange={handleSubtaskTextInputChange}
            onKeyDown={onKeyDown}
            className="relative z-10 h-9 border-none bg-transparent px-0 text-[13px] font-medium shadow-none focus-visible:ring-0 text-foreground placeholder:text-muted-foreground/60"
          />
        </div>
        <div className="flex items-center gap-1.5 pr-1">
          <AnimatePresence>
            {(subtaskText.trim() || subtaskCompletion) && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="flex items-center gap-1"
              >
                <div className="hidden sm:flex items-center gap-1 px-2 py-1 rounded bg-muted text-[10px] font-bold text-muted-foreground border border-border/20">
                  <CornerDownLeft className="h-2.5 w-2.5" />
                  <span>ENTER</span>
                </div>
                <Button
                  size="icon"
                  className="h-8 w-8 rounded-xl bg-primary shadow-md hover:bg-primary/90 transition-all active:scale-95"
                  onClick={(e) => {
                    e.preventDefault()
                    handleAddSubtask?.()
                  }}
                >
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="space-y-2">
        {subtasks.map((subtask) => (
          <SubtaskRow
            key={subtask.id}
            subtask={subtask}
            handleToggleSubtaskClick={handleToggleSubtaskClick}
            handleToggleSubtaskKeyDown={handleToggleSubtaskKeyDown}
            handleDeleteSubtaskClick={handleDeleteSubtaskClick}
          />
        ))}
        {subtasks.length === 0 && (
          <div className="flex flex-col items-center justify-center py-6 text-muted-foreground/40 border-2 border-dashed border-border/30 rounded-2xl">
            <ListTree className="h-8 w-8 mb-2 opacity-20" />
            <p className="text-xs font-medium">No subtasks yet</p>
          </div>
        )}
      </div>

      {totalSubtasks > 0 && (
        <SubtaskProgress completedSubtasks={completedSubtasks} totalSubtasks={totalSubtasks} />
      )}
    </div>
  )
}

const METHOD_ICONS = {
  push: Bell,
  sms: Smartphone,
  email: Mail,
  social: Share2,
}

const ReminderRow = ({ reminder, index, onRemove }) => {
  const remindAt = typeof reminder === 'object' ? reminder.remindAt : reminder
  const method = typeof reminder === 'object' ? reminder.method : 'push'

  const formattedReminder =
    typeof remindAt === 'number' || !isNaN(new Date(remindAt).getTime())
      ? format(new Date(remindAt), 'MMM d, h:mm a')
      : remindAt

  const Icon = METHOD_ICONS[method] || Bell

  return (
    <div className="flex items-center gap-2 rounded-md border bg-background p-1.5 text-[11px] shadow-sm">
      <div className="flex h-5 w-5 items-center justify-center rounded bg-primary/10 text-primary">
        <Icon className="h-3 w-3" />
      </div>
      <div className="flex flex-col min-w-0">
        <span className="truncate font-medium">{formattedReminder}</span>
        <span className="text-[9px] uppercase text-muted-foreground font-bold tracking-tight">
          {method}
        </span>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="ml-auto h-5 w-5 text-muted-foreground hover:text-destructive shrink-0"
        data-index={index}
        onClick={onRemove}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  )
}

const WhenTabPanel = ({
  todo,
  handleStartDateChange,
  handleEndDateChange,
  handleRecurrenceChange,
  handleAddReminder,
  reminderEntries,
  handleRemoveReminder,
}) => {
  const [selectedMethod, setSelectedMethod] = useState('push')

  const handleAddClick = async (date) => {
    if (!date) return

    if (selectedMethod === 'push') {
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission()
        if (permission === 'granted') {
          toast.success('Push notifications enabled!', {
            description: 'You will now receive reminders in this browser.',
          })
        } else {
          toast.error('Permission denied', {
            description:
              'Please enable notifications in your browser settings to receive push reminders.',
          })
          return
        }
      } else if (Notification.permission === 'denied') {
        toast.error('Notifications blocked', {
          description:
            'Please enable notifications in your browser settings to receive push reminders.',
        })
        return
      }
    }

    handleAddReminder(date, selectedMethod)
  }

  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2 py-1">
        <Calendar className="h-3.5 w-3.5 text-primary" />
        <p className="text-[11px] font-bold uppercase tracking-wider text-primary">Schedule</p>
        <div className="h-px flex-1 bg-border/10" />
      </div>
      <div className="grid gap-2">
        <div className="space-y-1">
          <Label className="text-[11px] text-muted-foreground">Start</Label>
          <DateTimePicker
            date={todo.startDate ? new Date(todo.startDate) : undefined}
            setDate={handleStartDateChange}
            placeholder="Pick start"
            className="bg-muted/40 border-transparent hover:bg-muted/60 text-left font-normal text-xs text-foreground data-[placeholder]:text-muted-foreground"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px] text-muted-foreground">End</Label>
          <DateTimePicker
            date={todo.endDate ? new Date(todo.endDate) : undefined}
            setDate={handleEndDateChange}
            placeholder="Pick end"
            className="bg-muted/40 border-transparent hover:bg-muted/60 text-left font-normal text-xs text-foreground data-[placeholder]:text-muted-foreground"
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor={`repeat-${todo.id}`} className="text-[11px] text-muted-foreground">
          Repeat
        </Label>
        <select
          id={`repeat-${todo.id}`}
          value={todo.recurrence || 'none'}
          onChange={(e) => handleRecurrenceChange(e.target.value)}
          className="h-8 w-full rounded-md border-transparent bg-muted/40 px-2 text-xs outline-none focus:ring-2 focus:ring-ring hover:bg-muted/60 transition-colors text-foreground"
        >
          <option value="none">Does not repeat</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>

      <div className="flex items-center gap-2 pt-4 pb-1">
        <Bell className="h-3.5 w-3.5 text-primary" />
        <p className="text-[11px] font-bold uppercase tracking-wider text-primary">Reminders</p>
        <div className="h-px flex-1 bg-border/10" />
      </div>

      <div className="space-y-3 p-3 rounded-xl bg-muted/20 border border-border/20">
        <div className="space-y-1.5">
          <Label className="text-[10px] uppercase font-bold text-muted-foreground/70 tracking-tight">
            Notification Method
          </Label>
          <div className="grid grid-cols-4 gap-1.5">
            {[
              { id: 'push', icon: Bell, label: 'Push' },
              { id: 'sms', icon: Smartphone, label: 'SMS', disabled: true },
              { id: 'email', icon: Mail, label: 'Email', disabled: true },
              { id: 'social', icon: Share2, label: 'Social', disabled: true },
            ].map((method) => {
              const Icon = method.icon
              const isSelected = selectedMethod === method.id
              return (
                <Tooltip key={method.id}>
                  <TooltipTrigger asChild>
                    <button
                      disabled={method.disabled}
                      onClick={() => setSelectedMethod(method.id)}
                      className={cn(
                        'flex flex-col items-center justify-center py-2 px-1 rounded-lg border transition-all gap-1',
                        isSelected
                          ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                          : 'bg-background text-muted-foreground border-border/20 hover:border-primary/30',
                        method.disabled && 'opacity-40 cursor-not-allowed grayscale',
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span className="text-[9px] font-bold uppercase tracking-tighter">
                        {method.label}
                      </span>
                    </button>
                  </TooltipTrigger>
                  {method.disabled && (
                    <TooltipContent side="bottom">
                      <p className="text-[10px]">Coming soon</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              )
            })}
          </div>
        </div>

        <DateTimePicker
          date={undefined}
          setDate={handleAddClick}
          placeholder="Set time"
          showConfirm={true}
          trigger={
            <Button className="w-full h-8 rounded-lg bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider shadow-sm hover:shadow-md transition-all active:scale-95">
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add Reminder
            </Button>
          }
        />

        {selectedMethod === 'push' &&
          typeof window !== 'undefined' &&
          'Notification' in window &&
          Notification.permission === 'default' && (
            <p className="text-[10px] text-muted-foreground text-center animate-pulse italic">
              * Browser will ask for permission
            </p>
          )}
      </div>

      <div className="space-y-2 mt-2">
        {reminderEntries.map((entry) => (
          <ReminderRow
            key={entry.id}
            reminder={entry.value}
            index={entry.index}
            onRemove={handleRemoveReminder}
          />
        ))}
        {reminderEntries.length === 0 && (
          <p className="text-[11px] text-muted-foreground text-center py-2">
            No reminders set for this task.
          </p>
        )}
      </div>
    </div>
  )
}

const WhereTabPanel = ({ todo, handleAddLocation, handleRemoveLocation, handleUpdateLocation }) => (
  <Suspense fallback={<Skeleton className="h-48 w-full rounded-xl" />}>
    <TaskLocations
      locations={todo.locations || []}
      onAdd={handleAddLocation}
      onRemove={handleRemoveLocation}
      onUpdate={handleUpdateLocation}
    />
  </Suspense>
)

const TeammateSelector = ({ todo, handleToggleAssignee }) => {
  const snap = useSnapshot(store)
  const [open, setOpen] = useState(false)
  const teammates = snap.teammates || []

  const selectedTeammates = teammates.filter((t) => (todo.assignees || []).includes(t.name))

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-auto min-h-[36px] py-1.5 w-full justify-start bg-muted/40 border-transparent hover:bg-muted/60 text-xs font-medium text-foreground transition-all rounded-xl px-3"
        >
          {selectedTeammates.length > 0 ? (
            <div className="flex items-center gap-1.5 flex-wrap">
              <div className="flex -space-x-2 overflow-hidden mr-1">
                {selectedTeammates.map((teammate) => (
                  <img
                    key={teammate.id}
                    src={teammate.avatar}
                    alt={teammate.name}
                    className="inline-block h-5 w-5 rounded-full ring-2 ring-background object-cover"
                  />
                ))}
              </div>
              <span className="truncate max-w-[150px]">
                {selectedTeammates.length === 1
                  ? selectedTeammates[0].name
                  : `${selectedTeammates.length} people`}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <User className="h-4 w-4" />
              <span>Assign to...</span>
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0 bg-background/80 backdrop-blur-xl border border-border/20 shadow-2xl rounded-2xl overflow-hidden"
        align="start"
      >
        <Command className="bg-transparent">
          <CommandInput
            placeholder="Search for name"
            className="h-10 text-xs border-none focus:ring-0"
          />
          <CommandList className="max-h-64 no-scrollbar">
            <CommandEmpty className="py-4 text-[11px] text-muted-foreground text-center">
              No teammates found.
            </CommandEmpty>
            <CommandGroup heading="Teammates" className="px-2">
              {teammates.map((teammate) => {
                const isSelected = (todo.assignees || []).includes(teammate.name)
                return (
                  <CommandItem
                    key={teammate.id}
                    value={teammate.name}
                    onSelect={() => {
                      handleToggleAssignee(teammate.name)
                    }}
                    className="flex items-center gap-2.5 px-2 py-2 cursor-pointer hover:bg-muted/50 rounded-xl transition-colors data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary"
                  >
                    <div className="relative">
                      <img
                        src={teammate.avatar}
                        alt={teammate.name}
                        className="h-6 w-6 rounded-full object-cover shadow-sm"
                      />
                      {isSelected && (
                        <div className="absolute -right-1 -bottom-1 bg-primary text-primary-foreground rounded-full p-0.5 shadow-sm">
                          <CheckCircle2 className="h-2 w-2" />
                        </div>
                      )}
                    </div>
                    <span className={cn('text-[13px] font-medium', isSelected && 'text-primary')}>
                      {teammate.name}
                    </span>
                    {isSelected && <CheckCircle2 className="h-3.5 w-3.5 ml-auto text-primary" />}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

const WhoTabPanel = ({ todo, handleToggleAssignee }) => (
  <div className="space-y-4">
    <TeammateSelector todo={todo} handleToggleAssignee={handleToggleAssignee} />
  </div>
)

const AttachmentsTabPanel = ({
  attachmentEntries,
  handleRemoveAttachment,
  handleAttachmentChange,
  handleDownloadAttachment,
  fileInputRef,
}) => {
  return (
    <div className="space-y-2">
      <AttachmentViewer
        attachmentEntries={attachmentEntries}
        onRemove={handleRemoveAttachment}
        onDownload={handleDownloadAttachment}
      />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleAttachmentChange}
      />
    </div>
  )
}

const tabBodyComponents = {
  subtasks: SubtasksTabPanel,
  when: WhenTabPanel,
  where: WhereTabPanel,
  who: WhoTabPanel,
  attachments: AttachmentsTabPanel,
}

function TabPanelContent({
  activeTab,
  todo,
  subtaskText,
  subtaskCompletion,
  acceptSubtaskCompletion,
  reminderEntries,
  attachmentEntries,
  handleSubtaskTextInputChange,
  handleSubtaskInputKeyDown,
  handleAddSubtask,
  handleToggleSubtaskClick,
  handleToggleSubtaskKeyDown,
  handleDeleteSubtaskClick,
  handleStartDateChange,
  handleEndDateChange,
  handleRecurrenceChange,
  handleToggleAssignee,
  handleAddLocation,
  handleRemoveLocation,
  handleUpdateLocation,
  handleAddReminder,
  handleUploadClick,
  handleRemoveReminder,
  handleRemoveAttachment,
  handleAttachmentChange,
  handleDownloadAttachment,
  handleDownloadAll,
  fileInputRef,
}) {
  const attachmentAction = (
    <div className="flex items-center gap-2">
      {attachmentEntries.length > 0 && (
        <Button
          size="xs"
          variant="ghost"
          className="h-7 px-2 text-primary hover:bg-primary/10 transition-colors"
          onClick={handleDownloadAll}
        >
          <Download className="h-3.5 w-3.5 mr-1" />
          <span className="text-[11px] font-semibold">Download all</span>
        </Button>
      )}
      <Button
        size="xs"
        variant="ghost"
        className="h-7 px-2 hover:bg-muted transition-colors"
        onClick={handleUploadClick}
      >
        <Plus className="h-3.5 w-3.5 mr-1" />
        <span className="text-[11px] font-semibold">Add files</span>
      </Button>
    </div>
  )

  const panelActions = {
    attachments: attachmentAction,
  }

  const panelPropsByTab = {
    subtasks: {
      todo,
      subtaskText,
      subtaskCompletion,
      acceptSubtaskCompletion,
      handleSubtaskTextInputChange,
      handleSubtaskInputKeyDown,
      handleAddSubtask,
      handleToggleSubtaskClick,
      handleToggleSubtaskKeyDown,
      handleDeleteSubtaskClick,
    },
    when: {
      todo,
      handleStartDateChange,
      handleEndDateChange,
      handleRecurrenceChange,
      handleAddReminder,
      reminderEntries,
      handleRemoveReminder,
    },
    where: {
      todo,
      handleAddLocation,
      handleRemoveLocation,
      handleUpdateLocation,
    },
    who: {
      todo,
      handleToggleAssignee,
    },
    attachments: {
      attachmentEntries,
      handleRemoveAttachment,
      handleAttachmentChange,
      handleDownloadAttachment,
      fileInputRef,
    },
  }

  const PanelBodyComponent = tabBodyComponents[activeTab]
  const panelProps = panelPropsByTab[activeTab]

  if (!PanelBodyComponent || !panelProps) {
    return null
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={tabPanelTransition}
        className="space-y-3 flex flex-col"
      >
        {panelActions[activeTab] ? (
          <div className="flex justify-end">{panelActions[activeTab]}</div>
        ) : null}
        <PanelBodyComponent {...panelProps} />
      </motion.div>
    </AnimatePresence>
  )
}

export function TodoItemInfoTabs({
  todo,
  tabs,
  activeTab,
  isBoard,
  onTabChange,
  onSurfaceInteraction,
  subtaskText,
  subtaskCompletion,
  acceptSubtaskCompletion,
  reminderEntries,
  attachmentEntries,
  handleSubtaskTextInputChange,
  handleSubtaskInputKeyDown,
  handleAddSubtask,
  handleToggleSubtaskClick,
  handleToggleSubtaskKeyDown,
  handleDeleteSubtaskClick,
  handleStartDateChange,
  handleEndDateChange,
  handleRecurrenceChange,
  handleToggleAssignee,
  handleAddLocation,
  handleRemoveLocation,
  handleUpdateLocation,
  handleAddReminder,
  handleUploadClick,
  handleRemoveReminder,
  handleRemoveAttachment,
  handleAttachmentChange,
  handleDownloadAttachment,
  handleDownloadAll,
  fileInputRef,
}) {
  const tabsListRef = useRef(null)
  const tabByValue = useMemo(() => Object.fromEntries(tabs.map((tab) => [tab.value, tab])), [tabs])
  const activeTabMeta = tabByValue[activeTab] || tabs[0]
  const indicatorLayoutId = `todo-info-tab-indicator-${todo.id}`

  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)
  const [canScroll, setCanScroll] = useState(false)

  const checkScrollability = () => {
    const tabsListNode = tabsListRef.current
    if (!tabsListNode) return

    const { scrollLeft, scrollWidth, clientWidth } = tabsListNode
    setCanScroll(scrollWidth > clientWidth)
    setShowLeftArrow(scrollLeft > 0)
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1)
  }

  const scrollTabs = (direction) => {
    const tabsListNode = tabsListRef.current
    if (!tabsListNode) return

    const scrollAmount = 150
    tabsListNode.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    checkScrollability()

    const tabsListNode = tabsListRef.current
    if (!tabsListNode) return

    const handleScroll = () => {
      checkScrollability()
    }

    const resizeObserver = new ResizeObserver(checkScrollability)
    resizeObserver.observe(tabsListNode)

    tabsListNode.addEventListener('scroll', handleScroll)

    return () => {
      resizeObserver.disconnect()
      tabsListNode.removeEventListener('scroll', handleScroll)
    }
  }, [tabs])

  useEffect(() => {
    const tabsListNode = tabsListRef.current
    if (!tabsListNode) {
      return undefined
    }

    const handleNativeWheel = (event) => {
      const scrollDelta = event.deltaX + event.deltaY

      if (scrollDelta === 0) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      tabsListNode.scrollLeft += scrollDelta
    }

    tabsListNode.addEventListener('wheel', handleNativeWheel, { passive: false })

    return () => {
      tabsListNode.removeEventListener('wheel', handleNativeWheel)
    }
  }, [])

  return (
    <motion.div
      layout="position"
      onClick={onSurfaceInteraction}
      onPointerDown={onSurfaceInteraction}
      className={cn(
        'mb-2 w-full min-w-0 max-w-full overflow-hidden rounded-2xl p-2 flex flex-col',
        isBoard && 'mb-1.5 p-1.5',
      )}
    >
      <Tabs value={activeTab} onValueChange={onTabChange} className="gap-1 flex flex-col">
        <div className="relative">
          {/* Left Arrow */}
          {canScroll && showLeftArrow && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-7 w-7 rounded-full bg-background/80 backdrop-blur-sm border border-border/20 shadow-md hover:bg-background md:hidden"
              onClick={() => scrollTabs('left')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}

          {/* Right Arrow */}
          {canScroll && showRightArrow && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-7 w-7 rounded-full bg-background/80 backdrop-blur-sm border border-border/20 shadow-md hover:bg-background md:hidden"
              onClick={() => scrollTabs('right')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}

          <TabsList
            ref={tabsListRef}
            className={cn(
              'h-auto w-full max-w-full justify-start gap-1 overflow-x-auto overflow-y-visible no-scrollbar touch-pan-x rounded-xl p-1 bg-transparent',
              canScroll && 'px-8 md:px-1',
            )}
          >
            {tabs.map((tab) => (
              <TabTrigger
                key={tab.value}
                tab={tab}
                activeTab={activeTab}
                indicatorLayoutId={indicatorLayoutId}
              />
            ))}
          </TabsList>
        </div>

        <div className="min-w-0 max-w-full overflow-hidden rounded-xl pt-2 pb-0 px-4 flex flex-col">
          <TabPanelContent
            activeTab={activeTabMeta.value}
            todo={todo}
            subtaskText={subtaskText}
            subtaskCompletion={subtaskCompletion}
            acceptSubtaskCompletion={acceptSubtaskCompletion}
            reminderEntries={reminderEntries}
            attachmentEntries={attachmentEntries}
            handleSubtaskTextInputChange={handleSubtaskTextInputChange}
            handleSubtaskInputKeyDown={handleSubtaskInputKeyDown}
            handleAddSubtask={handleAddSubtask}
            handleToggleSubtaskClick={handleToggleSubtaskClick}
            handleToggleSubtaskKeyDown={handleToggleSubtaskKeyDown}
            handleDeleteSubtaskClick={handleDeleteSubtaskClick}
            handleStartDateChange={handleStartDateChange}
            handleEndDateChange={handleEndDateChange}
            handleRecurrenceChange={handleRecurrenceChange}
            handleToggleAssignee={handleToggleAssignee}
            handleAddLocation={handleAddLocation}
            handleRemoveLocation={handleRemoveLocation}
            handleUpdateLocation={handleUpdateLocation}
            handleAddReminder={handleAddReminder}
            handleUploadClick={handleUploadClick}
            handleRemoveReminder={handleRemoveReminder}
            handleRemoveAttachment={handleRemoveAttachment}
            handleAttachmentChange={handleAttachmentChange}
            handleDownloadAttachment={handleDownloadAttachment}
            handleDownloadAll={handleDownloadAll}
            fileInputRef={fileInputRef}
          />
        </div>
      </Tabs>
    </motion.div>
  )
}
